import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatSize, jsonFieldError } from 'common/packages';

const dropzoneBase = {
  padding: 20,
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#757575',
  outline: 'none',
  cursor: 'pointer',
  transition: 'border .24s ease-in-out',
};

const UploadForm = (props) => {
  const { application, submitting, onSubmit } = props;

  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [icon, setIcon] = useState(null);
  const [values, setValues] = useState({
    packageName: '',
    version: '',
    type: '',
    label: '',
    description: '',
    compatibility: '',
    manifest: '',
    signature: '',
    isPublic: false,
    isVisible: true,
  });
  const [blurred, setBlurred] = useState({});

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    multiple: false,
    onDrop: (accepted) => {
      if (accepted.length > 0) setFile(accepted[0]);
    },
  });

  const dropzoneStyle = useMemo(
    () => ({
      ...dropzoneBase,
      ...(isDragActive ? { borderColor: '#2196f3' } : {}),
      ...(isDragReject ? { borderColor: '#ff1744' } : {}),
    }),
    [isDragActive, isDragReject]
  );

  const set = (field) => (event) =>
    setValues((previous) => ({ ...previous, [field]: event.target.value }));

  const toggle = (field) => (event) =>
    setValues((previous) => ({ ...previous, [field]: event.target.checked }));

  const compatibilityError = jsonFieldError(values.compatibility, true);
  const manifestError = jsonFieldError(values.manifest, false);

  const required = file && values.packageName.trim() && values.version.trim() && values.type.trim();
  const canSubmit = Boolean(required) && !compatibilityError && !manifestError && !submitting;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) onSubmit({ ...values, file, thumbnail, icon });
      }}
    >
      <Card>
        <CardHeader
          title="Upload a package"
          subheader="Uploading never publishes. Publish stays a separate, deliberate action."
        />
        <Divider />
        <CardContent>
          <Box {...getRootProps({ style: dropzoneStyle })}>
            <input {...getInputProps()} />
            <Typography variant="body1" align="center">
              {file
                ? `${file.name} — ${formatSize(file.size)}`
                : "Drag the artifact here, or click to choose a file"}
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="Package name"
                value={values.packageName}
                onChange={set('packageName')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="Version"
                value={values.version}
                onChange={set('version')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                size="small"
                label="Type"
                helperText="Free text, e.g. installable-windows"
                value={values.type}
                onChange={set('type')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Label"
                helperText="Defaults to the file name"
                value={values.label}
                onChange={set('label')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Signature"
                value={values.signature}
                onChange={set('signature')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Description"
                value={values.description}
                onChange={set('description')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Compatibility"
                placeholder='["version expression","semver expression"]'
                value={values.compatibility}
                onChange={set('compatibility')}
                onBlur={() => setBlurred((p) => ({ ...p, compatibility: true }))}
                error={Boolean(blurred.compatibility && compatibilityError)}
                helperText={(blurred.compatibility && compatibilityError) || ' '}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Manifest"
                value={values.manifest}
                onChange={set('manifest')}
                onBlur={() => setBlurred((p) => ({ ...p, manifest: true }))}
                error={Boolean(blurred.manifest && manifestError)}
                helperText={(blurred.manifest && manifestError) || ' '}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>
                Thumbnail (PNG)
              </Typography>
              <input
                type="file"
                accept="image/png"
                onChange={(event) => setThumbnail(event.target.files[0] || null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>
                Icon (PNG)
              </Typography>
              <input
                type="file"
                accept="image/png"
                onChange={(event) => setIcon(event.target.files[0] || null)}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start' }}
                  control={<Checkbox checked={values.isPublic} onChange={toggle('isPublic')} />}
                  label="Public — anyone can download it, anonymous callers included. Unchecked: only accounts on the access list, and only if they are licensed for this application."
                />
                <FormControlLabel
                  control={<Checkbox checked={values.isVisible} onChange={toggle('isVisible')} />}
                  label="Visible"
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button component={RouterLink} to={`/packages/${application}`}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disableElevation disabled={!canSubmit}>
            Register and upload
          </Button>
        </CardActions>
      </Card>
    </form>
  );
};

UploadForm.propTypes = {
  application: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

export default UploadForm;
